import axios from 'axios';
import { environment } from '../environments/environment';
import { v4 as uuidv4 } from 'uuid';

const BASE_URL = `${environment.baseUrl}/user-activity`;

// Create a session ID when the service is first imported
const SESSION_ID = uuidv4();

/**
 * Get device information for tracking
 * @returns {Object} Device information
 */
const getDeviceInfo = () => {
  const deviceInfo = {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
  };
  
  return deviceInfo;
};

/**
 * Track a user activity
 * @param {Object} activityData - Activity data to track
 * @returns {Promise} - The API response
 */
export const trackUserActivity = async (activityData) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('Cannot track activity: User not authenticated');
      return;
    }
    
    const response = await axios.post(
      `${BASE_URL}/track`,
      {
        ...activityData,
        sessionId: SESSION_ID,
        deviceInfo: getDeviceInfo(),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error tracking user activity:', error);
    // We don't want to throw errors for activity tracking to avoid affecting user experience
    return null;
  }
};

/**
 * Track a page view
 * @param {String} pageName - Name of the page
 * @param {String} path - Path of the page
 * @param {Object} metadata - Additional metadata
 */
export const trackPageView = async (pageName, path, metadata = {}) => {
  return trackUserActivity({
    activityType: 'PAGE_VIEW',
    page: pageName,
    path,
    metadata,
  });
};

/**
 * Track a button click
 * @param {String} pageName - Name of the page
 * @param {String} path - Path of the page
 * @param {String} componentName - Name of the component
 * @param {Object} actionDetails - Details about the action
 */
export const trackButtonClick = async (pageName, path, componentName, actionDetails = {}) => {
  return trackUserActivity({
    activityType: 'BUTTON_CLICK',
    page: pageName,
    path,
    componentName,
    actionDetails,
  });
};

/**
 * Track a form submission
 * @param {String} pageName - Name of the page
 * @param {String} path - Path of the page
 * @param {String} formName - Name of the form
 * @param {Object} actionDetails - Details about the form submission
 */
export const trackFormSubmit = async (pageName, path, formName, actionDetails = {}) => {
  return trackUserActivity({
    activityType: 'FORM_SUBMIT',
    page: pageName,
    path,
    componentName: formName,
    actionDetails,
  });
};

/**
 * Track a post action (create, like, comment, etc.)
 * @param {String} pageName - Name of the page
 * @param {String} path - Path of the page
 * @param {String} actionType - Type of post action
 * @param {Object} actionDetails - Details about the post action
 */
export const trackPostAction = async (pageName, path, actionType, actionDetails = {}) => {
  return trackUserActivity({
    activityType: 'POST_ACTION',
    page: pageName,
    path,
    componentName: actionType,
    actionDetails,
  });
};

/**
 * Get user activity analytics
 * @param {Object} timeRange - Time range for analytics
 * @returns {Promise} - The API response
 */
export const getUserActivityAnalytics = async (timeRange = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('Cannot get analytics: User not authenticated');
      return null;
    }
    
    const { startDate, endDate } = timeRange;
    let url = `${BASE_URL}/analytics`;
    
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`;
    }
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error getting user activity analytics:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return null; // Return null instead of throwing to prevent component crashes
  }
};

/**
 * Get user activities
 * @param {Object} params - Query parameters
 * @returns {Promise} - The API response
 */
export const getUserActivities = async (params = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('Cannot get activities: User not authenticated');
      return null;
    }
    
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    
    const url = `${BASE_URL}/user?${queryParams.toString()}`;
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error getting user activities:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return null; // Return null instead of throwing to prevent component crashes
  }
};

/**
 * Admin function to get activities for a specific user
 * @param {String} userId - User ID to get activities for
 * @param {Object} params - Query parameters
 * @returns {Promise} - The API response
 */
export const getActivitiesForUser = async (userId, params = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('Cannot get activities: Admin not authenticated');
      return null;
    }
    
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    
    const url = `${BASE_URL}/admin/user/${userId}?${queryParams.toString()}`;
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error getting activities for user:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return null;
  }
};

/**
 * Test the user activity API connectivity
 * @returns {Promise} - The API response
 */
export const testUserActivityApi = async () => {
  try {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('Cannot test API: User not authenticated');
      return { success: false, message: 'No authentication token available' };
    }
    
    const url = `${BASE_URL}/debug-auth`;
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    return { 
      success: true, 
      message: 'API connection successful', 
      data: response.data 
    };
  } catch (error) {
    console.error('Error testing user activity API:', error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return { 
      success: false, 
      message: `API connection failed: ${error.message}`,
      error: error
    };
  }
}; 